// Pentest Scope — FE-only preview
// --------------------------------
// Display-only replica of the DeepDiscovery backend's scope decision, used by
// the Add-Application "Pentest Scope" step to show the user what will and won't
// be tested as they type. This NEVER calls the backend and is NOT a security
// gate — the backend's WebAppUnderTestConfiguration.isURLInScope remains the
// sole authority. Its only job is to make the (subtle) scope rules legible.
//
// Replicates:
//   * Web-app scope  — WebAppUnderTestConfiguration._deriveSubdomainScope /
//                      _isWithinWebAppScope. Registrable-domain detection uses
//                      the Public Suffix List via `tldts`, mirroring the
//                      backend's `tldextract` (handles co.uk, com.au, etc.).
//   * API-domain scope — _isWithinApiDomainScope / _isPathUnderPrefix:
//                      host-exact + path-prefix (whole-segment, case-insensitive).
//
// Parsing of API-domain entries is shared with the Verify step via
// [apiDomains.ts] so the two surfaces never disagree.

import { parse as parseHost } from "tldts";
import {
  canonicalApiDomain,
  parseApiDomainEntry,
  toApiDomainList,
} from "@/utils/apiDomains";

// www is the one subdomain the backend treats as whole-domain ownership
// (alongside the bare apex) — see _deriveSubdomainScope.
const WWW_SUBDOMAIN = "www";

export type WebAppScopeMode = "all-subdomains" | "single-host" | "exact-host";

export interface WebAppScope {
  mode: WebAppScopeMode;
  // The app hostname, lowercased (e.g. "app.example.com").
  host: string;
  // The registrable domain / eTLD+1 (e.g. "example.com"); "" for exact-host
  // targets (IP, localhost, hosts with no public suffix).
  registrableDomain: string;
  // Human-readable one-liner describing what the web-app grant covers.
  description: string;
}

export interface ApiDomainScopeItem {
  // Canonical "host" or "host/path" — the label shown to the user.
  label: string;
  host: string;
  // "" means whole-host scope; otherwise the base path (e.g. "/rest").
  basePath: string;
  description: string;
}

export interface PentestScopePreview {
  // null until the entered URL yields a usable host.
  webApp: WebAppScope | null;
  apiDomains: ApiDomainScopeItem[];
}

// Derive the web-app scope from the entered app URL. Returns null when no host
// can be parsed yet (caller shows nothing rather than a misleading default).
//
// Decision tree mirrors _deriveSubdomainScope:
//   * IP literal, or host with no registrable domain (localhost, internal) ->
//     exact-host (only that exact hostname).
//   * apex (hostname == registrable domain) or www.<registrable domain> ->
//     all-subdomains of the registrable domain.
//   * any other specific subdomain -> only that exact host.
export function deriveWebAppScope(url: string | undefined): WebAppScope | null {
  const parsed = parseHost(url || "");
  const host = (parsed.hostname || "").toLowerCase();
  if (!host) return null;

  // Exact-host: IP targets and anything without a registrable domain. No
  // concept of subdomains — only this exact host is in scope.
  if (parsed.isIp || !parsed.domain) {
    return {
      mode: "exact-host",
      host,
      registrableDomain: "",
      description: `Only ${host} is in scope (exact host match).`,
    };
  }

  const registrableDomain = parsed.domain.toLowerCase();
  const subdomain = (parsed.subdomain || "").toLowerCase();

  // Apex ("" subdomain) or www -> the customer owns the whole registered
  // domain, so every host under it is in scope.
  if (subdomain === "" || subdomain === WWW_SUBDOMAIN) {
    return {
      mode: "all-subdomains",
      host,
      registrableDomain,
      description: `Every host under ${registrableDomain} is in scope — the domain and all of its subdomains.`,
    };
  }

  // A specific subdomain: only this exact host. Siblings are out of scope
  // unless separately declared as an API domain below.
  return {
    mode: "single-host",
    host,
    registrableDomain,
    description:
      `Only ${host} is in scope. Other subdomains of ${registrableDomain} ` +
      `are not tested unless you add them as an API domain below.`,
  };
}

// Build one scope item per configured API domain. De-duplicated by canonical
// "host/path" key (case-insensitive), matching getVerificationTargets and the
// backend's _normalizeApiDomains dedupe. Entries with no derivable host are
// dropped.
export function buildApiDomainScopeItems(rawApiDomains: unknown): ApiDomainScopeItem[] {
  const items: ApiDomainScopeItem[] = [];
  const seen = new Set<string>();

  for (const entry of toApiDomainList(rawApiDomains)) {
    const parsed = parseApiDomainEntry(entry);
    if (!parsed) continue;
    const label = canonicalApiDomain(parsed);
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const description = parsed.basePath
      ? `Only ${parsed.host} at ${parsed.basePath} and paths under it (e.g. ${parsed.basePath}/users) ` +
        `are in scope. Host-exact — ${parsed.host} is not any other subdomain — and no other paths on ` +
        `${parsed.host} are tested.`
      : `Only ${parsed.host} is in scope (all paths). Host-exact — ${parsed.host} is not any other subdomain.`;

    items.push({ label, host: parsed.host, basePath: parsed.basePath, description });
  }

  return items;
}

// Full preview for the Pentest Scope step: the web-app grant plus each API
// domain's grant. Pure — no network, no side effects.
export function buildPentestScopePreview(
  url: string | undefined,
  rawApiDomains: unknown,
): PentestScopePreview {
  return {
    webApp: deriveWebAppScope(url),
    apiDomains: buildApiDomainScopeItems(rawApiDomains),
  };
}
